/*
 * Realtime - socket.io connection for realtime notification of server events
 *
 * - Listens to events from the server and announces them to the subscribers
 *
 * Created by Per Moeller <pm@telecomx.dk> on 202-03-23
 * Updated to Typescript by Per Moeller <pm@telecomx.dk> on 2025-01-24
 */

import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useSettingsStore } from '@/stores/Settings'
import { useElectronStore } from '@/stores/Electron'

import globalEvents from '@/utilities/GlobalEvents'
import { UAParser } from 'ua-parser-js'
import logger from '@/utilities/Logger'
import { sleep } from '@/utilities/Tools'
import { shouldReconnectOnVisible, shouldRestartAfterLogin } from '@/utilities/ConnectionPolicy'

import type { RealtimeLogStreamRecipient } from './Realtime.types'
import Realtime, { RealtimeRequestType, SubscribeType, type RealtimeReply, type RealtimeReplyDataCommon, type RealtimeReplyDataCommunicatorGetStatus, type RealtimeRequest } from '@per_moeller/tcx-realtime-client'
import type { MediaDevice } from './MediaDevices.types'

/** The two guest request names the installed realtime client does not type yet - matched by string */
interface GuestRequestLike { request: string, data?: unknown }

/**
 * Republish a CommunicatorGuestReady/Left realtime request as a global event the host UI listens to. Kept module-level
 * so it can be tested without the store.
 * @param req The realtime request
 * @returns True if it was a guest request and was handled
 */
export function handleGuestRequest(req: GuestRequestLike): boolean {
	if (req.request === 'CommunicatorGuestReady') {
		globalEvents.emit('GuestInvite:Ready', req.data as { inviteId: string, guestName: string, expiresAt: string })
		return true
	}
	if (req.request === 'CommunicatorGuestLeft') {
		globalEvents.emit('GuestInvite:Left', req.data as { inviteId: string, guestName: string })
		return true
	}
	return false
}

export const useRealtimeStore = defineStore('realtime', () => {
	logger.debug('Realtime: Initializing store')

	const ss = useSettingsStore()
	const browserdetect = UAParser()
	const isEmb = ss.runtime.isEmbedded
	const connected = ref(false)
	const firstConnectCompleted = ref(false)
	const run = ref(false)
	const logStreamRecipients = ref<RealtimeLogStreamRecipient[]>([])
	let hasSubscribed = false
	/** Token the current connection authenticated with, so we can tell a real re-login from a token re-validation */
	let connectedWithToken: string | undefined

	const clientIdentifier = computed(() => {
		return `${ss.runtime.appName} Desktop ${ss.runtime.version}${isEmb ? '-' + ss.runtime.wrapperVersion : ''}${isEmb ? '' : ' ' + browserdetect.browser.name} on ${browserdetect.os.name}`
	})

	const realtime = new Realtime(ss.apiUrl.replace('https:', 'wss:').replace('http:', 'ws:'), ss.runtime.instanceId, clientIdentifier.value, ss.token, logger)
	const configEvents = realtime.configEvents
	const pbxEvents = realtime.pbxEvents
	const realtimeEvents = realtime.globalEvents

	globalEvents.on('Auth:LoggedIn', () => {
		// A throttled background tab re-validates its token every minute. Restarting then drops a
		// working connection and loses every event published while it comes back.
		if (shouldRestartAfterLogin({ embedded: isEmb, connected: connected.value, tokenChanged: connectedWithToken !== ss.token })) {
			restart()
		}
	})
	globalEvents.on('Auth:LoggedOut', originator => {
		if (originator !== 'REALTIME') { stop() }
	})

	// Failsafe feature - if realtime did not reconnect after wakeup via Auth:LoggedIn, this will try again after 5 seconds
	globalEvents.on('Wakeup', () => {
		setTimeout(() => {
			if (run.value && !connected.value) {
				logger.warning('Realtime: Performing emergency restart of realtime after wakeup event')
				restart()
			}
		}, 5000)
	})

	// A tab that lost its connection while hidden must not keep showing stale calls once it is on
	// screen again - reconnect at once instead of waiting for the reconnect backoff.
	if (typeof document !== 'undefined') {
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState !== 'visible') { return }
			if (shouldReconnectOnVisible({ embedded: isEmb, running: run.value, connected: connected.value })) {
				logger.warning('Realtime: Reconnecting because the window became visible while disconnected')
				restart()
			}
		})
	}

	/**
	 * Initialize the realtime connection - with a wait so that we can ensure it is ready before dependent pinia stores are spun up
	 */
	async function init() {
		while (!ss.mainInitCompleted) {
			await sleep(100)
		}

		await sleep(100)

		// realtime = new Realtime(ss.apiUrl.replace('https:', 'wss:').replace('http:', 'ws:'), ss.runtime.instanceId, clientIdentifier.value, ss.token, logger)

		realtime.globalEvents.on('Connected', () => {
			firstConnectCompleted.value = true
			globalEvents.emit('Realtime:Connected')
			logger.debug(`Realtime: Authenticated - subscribing to PBX and CONFIG-NOT-IPTV events for customer ${ss.auth?.customer}`)
			if (!hasSubscribed) {
				realtime?.subscribe(SubscribeType.PBX, ss.auth?.customer)
				realtime?.subscribe(SubscribeType['CONFIG-NOT-IPTV'], ss.auth?.customer)
				hasSubscribed = true
			}
			connected.value = true
		})

		realtime.globalEvents.on('Subscribed', data => {
			logger.debug(`Realtime: We are now subscribed to: ${data.type} for ${data.customer}`)
		})

		realtime.globalEvents.on('Disconnected', (_) => {
			connected.value = false
			globalEvents.emit('Realtime:Disconnected')
			logger.warning('Realtime: Disconnected')
		})

		realtime.globalEvents.on('Forbidden', message => {
			connected.value = false
			run.value = false
			ss.clear()
			globalEvents.emit('Auth:LoggedOut', 'REALTIME')
			logger.error(`Realtime: Forbidden: ${message}`)
			realtime?.stop()
			hasSubscribed = false
		})

		realtime.requestReplyEvents.on('*', (_, data) => {
			if (data.type === 'REQUEST') {
				requestHandler(data)
			} else {
				replyHandler(data)
			}
		})

		realtime.configEvents.on('EMPLOYEE_UPDATED', data => {
			if (data.id === ss.auth?.employee) {
				globalEvents.emit('MeUpdated', ss.token as string)
			}
		})

		// if (ss.isAuthenticated && ss.token) {
		// 	await start()
		// }
	}

	async function restart() {
		stop()
		if (!ss.isAuthenticated || !ss.token) { return }
		await start()
	}

	async function start() {
		if (run.value) { return }
		hasSubscribed = false
		connectedWithToken = ss.token as string
		await realtime?.start(ss.token as string, clientIdentifier.value)
		run.value = true
	}

	function stop() {
		if (!run.value) { return }
		realtime?.stop()
		run.value = false
		hasSubscribed = false
		connectedWithToken = undefined
	}

	async function requestHandler(req: RealtimeRequest) {
		logger.info(`Realtime: Got request for ${req.request} with id ${req.requestId}`)

		// The two guest requests are fire-and-forget (no reply): handle them before the requestId guard
		if (handleGuestRequest(req as unknown as GuestRequestLike)) { return }

		if (!req.requestId) { return }

		switch (req.request) {
			case RealtimeRequestType.CommunicatorLoggerGetHistory:
				if (req.replyTo) {
					realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, {
						success: true,
						log: logger.historyBuffer.map(o => {
							return { severity: logger.level2String(o.severity) as 'DEBUG' | 'INFO' | 'WARNING' | 'ERROR', message: o.message, when: o.when }
						})
					})
				}
				break

			case RealtimeRequestType.CommunicatorLoggerClearHistory:
				logger.historyBuffer = []
				if (req.replyTo) {
					realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, { success: true })
				}
				break

			case RealtimeRequestType.CommunicatorLoggerStartStream:
				if (req.replyTo) {
					if (!logger.streamCallback) { logger.streamCallback = streamLogEvent }
					const exists = logStreamRecipients.value.find(o => o.employee === req.replyTo?.employee && o.instanceId === req.replyTo.instanceId)
					if (!exists) {
						logStreamRecipients.value.push({ employee: req.replyTo.employee, instanceId: req.replyTo.instanceId, request: req.request, requestId: req.requestId })
					} else {
						exists.requestId = req.requestId // In case he lost connection and connected again, we need to have  the right requestId
					}
					realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, { success: true })
				}
				break

			case RealtimeRequestType.CommunicatorLoggerStopStream:
				if (req.replyTo) {
					const exists = logStreamRecipients.value.findIndex(o => o.employee === req.replyTo?.employee && o.instanceId === req.replyTo.instanceId)
					if (exists !== -1) {
						logStreamRecipients.value.splice(exists, 1)
					}
					realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, { success: true })
				}
				break

			case RealtimeRequestType.CommunicatorReload:
				if (req.replyTo) {
					realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, { success: true })
				}
				setTimeout(() => { location.reload() }, 500)
				break

			case RealtimeRequestType.CommunicatorTerminate:
				if (req.replyTo) {
					realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, { success: true })
				}

				setTimeout(() => {
					if (ss.runtime.isEmbedded) {
						const electron = useElectronStore()

						electron.terminate()
					} else {
						window.location.href = 'https://blank.page'
					}
				}, 500)
				break

			case RealtimeRequestType.CommunicatorDevtoolsShow:
				if (ss.runtime.isEmbedded) {
					try {
						const electron = useElectronStore()
						const res = await electron.devTools(true)

						if (req.replyTo) {
							realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, { success: res ?? false })
						}
					}
					catch (_) { /** Ignore */ }
				} else if (req.replyTo) {
					realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, { success: false, code: 'NOT_IN_APP', message: 'Running in browser' })
				}
				break

			case RealtimeRequestType.CommunicatorDevtoolsHide:
				if (ss.runtime.isEmbedded) {
					try {
						const electron = useElectronStore()

						const res = await electron.devTools(false)

						if (req.replyTo) {
							realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, { success: res ?? false })
						}
					}
					catch (_) { /** Ignore */ }
				} else if (req.replyTo) {
					realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, { success: false, code: 'NOT_IN_APP', message: 'Running in browser' })
				}
				break

			case RealtimeRequestType.CommunicatorImpersonate:
				if (req.replyTo && req.data?.token) {
					realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, { success: true })
				}
				setTimeout(() => {
					ss.logout(true)
				}, 500)
				setTimeout(() => {
					ss.loadAuth(req.data?.token).catch(() => { /** Ignore */ })
				}, 1000)
				break

			case RealtimeRequestType.CommunicatorGetStatus:
				if (req.replyTo) {
					try {
						const data = await collectStatus()

						data.success = true
						data.realtime = connected.value
						data.authenticated = ss.isAuthenticated
						data.appMode = ss.runtime.isEmbedded
						data.version = ss.runtime.version
						if (ss.runtime.isEmbedded) {
							data.version = `${ss.runtime.appName} Desktop ${ss.runtime.version}-${ss.runtime.wrapperVersion} on ${browserdetect.os.name}`
						} else {
							data.version = `${ss.runtime.appName} ${ss.runtime.version} ${browserdetect.browser.name} on ${browserdetect.os.name}`
						}
						data.theme = ss.settings.theme

						logger.info('Realtime: Sent reply for CommunicatorGetStatus')
						realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, data as unknown as RealtimeReplyDataCommunicatorGetStatus)
					}
					catch (err) { logger.error((err as Error).message) }
				}
				break

			case RealtimeRequestType.CommunicatorZeroTierStatus: { // Is it available and if so list of networks joined
				// Returns { success, data, code, message } (code/message if error)
				if (req.replyTo) {
					if (req.replyTo.employee !== ss.auth?.employee) {
						realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, { success: false, code: 'ACCESS_DENIED', message: 'Only permitted by user self' })
					}
					if (ss.runtime.isEmbedded) {
						try {
							const electron = useElectronStore()

							const res = await electron.zerotier('/status', 'GET')
							const res2 = await electron.zerotier('/network', 'GET')

							if (res2.success && res2.data) {
								const data = res2.data as { data: { id: string, status: string }[] }

								(res.data as RealtimeReplyDataCommon).networks = data.data.map((item: { id: string, status: string }) => { return { id: item.id, status: item.status } })
							} else {
								(res.data as RealtimeReplyDataCommon).networks = []
							}
							realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, res.data as RealtimeReplyDataCommon)
						}
						catch (err) { logger.error((err as Error).message) }
					} else {
						realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, { success: false, code: 'NOT_IN_APP', message: 'Running in browser' })
					}
				}
				break
			}

			case RealtimeRequestType.CommunicatorZeroTierJoin: { // Join a network
				// Returns { success, data, code, message } (code/message if error)
				if (req.replyTo) {
					if (req.replyTo.employee !== ss.auth?.employee) {
						realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, { success: false, code: 'ACCESS_DENIED', message: 'Only permitted by user self' })
					}
					if (ss.runtime.isEmbedded) {
						try {
							const electron = useElectronStore()
							const res = await electron.zerotier('/network/' + req.data?.network, 'POST')

							realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, res.data as RealtimeReplyDataCommon)
						}
						catch (err) { logger.error((err as Error).message) }
					} else {
						realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, { success: false, code: 'NOT_IN_APP', message: 'Running in browser' })
					}
				}
				break
			}

			case RealtimeRequestType.CommunicatorZeroTierLeave: { // Leave a network
				// Returns { success, data, code, message } (code/message if error)
				if (req.replyTo) {
					if (req.replyTo.employee !== ss.auth?.employee) {
						realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, { success: false, code: 'ACCESS_DENIED', message: 'Only permitted by user self' })
					}
					if (ss.runtime.isEmbedded) {
						try {
							const electron = useElectronStore()
							const res = await electron.zerotier('/network/' + req.data?.network, 'DELETE')

							realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, res.data as RealtimeReplyDataCommon)
						}
						catch (err) { logger.error((err as Error).message) }
					} else {
						realtime.sendReply(req.replyTo.employee, req.replyTo.instanceId, req.request, req.requestId, { success: false, code: 'NOT_IN_APP', message: 'Running in browser' })
					}
				}
				break
			}

			case RealtimeRequestType.CommunicatorCallAction: {
				switch (req.data?.action) {
					case 'CALL':
						globalEvents.emit('Call:Dial', { number: req.data.target as string })
						break
					case 'ANSWER':
						globalEvents.emit('Call:AnswerByCallerNumber', { callerNumber: req.data?.target as string })
						break
				}
			}
		}
	}

	// #region Status reporting

	let statusReport: Record<string, string | number | boolean | MediaDevice[] | null> = {}

	globalEvents.on('StatusReport', data => {
		statusReport[data.key] = data.value
	})

	/** Collect status from all participating modules */
	async function collectStatus() {
		statusReport = {}
		globalEvents.emit('StatusRequest')
		await sleep(500)
		return statusReport
	}

	// Here for future use - when we implement chat or similar
	function replyHandler(rep: RealtimeReply) { // eslint-disable-line
		// Currently we do not send out requests from here - so we will not get replies to handle - yet :-)
	}

	async function streamLogEvent(severity: string, message: string) {
		const failed = []

		for (const recipient of logStreamRecipients.value) {
			const success = await realtime.sendReply(recipient.employee, recipient.instanceId, recipient.request, recipient.requestId, {
				success: true,
				severity: severity as 'INFO' | 'WARNING' | 'ERROR' | 'DEBUG',
				message: message
			}, true)

			if (!success) {
				failed.push(recipient)
				logger.warning(`Realtime: Failed to stream log to ${recipient.employee} / ${recipient.instanceId} - now removed from recipent list`)
			}
		}

		for (const fail of failed) {
			const index = logStreamRecipients.value.findIndex(item => item === fail)
			if (index !== -1) { logStreamRecipients.value.splice(index, 1) }
		}
	}

	return { connected, run, configEvents, pbxEvents, realtimeEvents, firstConnectCompleted, init }
})
